import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OverviewSectionEdit = ({ data, onChange, errors, musicals = [], song, selectedCharacter, videoUrl = '', onVideoUrlChange, handleSaveLevel, handleCancelLevel, levelSaving, levelSaved, hasUnsavedChanges }) => {
  const handleInputChange = (field, value) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  return (
    <div className="space-y-4">
      {/* Mux Video URL for Overview */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <h3 className="text-xl font-heading font-semibold text-white mb-4 flex items-center space-x-2">
          <Icon name="Film" size={24} className="text-blue-200" />
          <span>Overview Video</span>
        </h3>
        <div>
          <label className="block text-sm font-medium text-blue-100 mb-2">
            Mux Playback ID
          </label>
          <input
            type="text"
            value={videoUrl}
            onChange={(e) => onVideoUrlChange?.(e?.target?.value)}
            className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="e.g. DS00Spx1CV902MCtPj5WknGlR017IEmVd"
          />
          <p className="text-blue-300 text-xs mt-1">
            Enter the Mux playback_id from your Mux dashboard or the videos table. Leave blank to hide the video for this level.
          </p>
        </div>
      </div>

      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <h3 className="text-xl font-heading font-semibold text-white mb-4 flex items-center space-x-2">
          <Icon name="FileText" size={24} className="text-blue-200" />
          <span>Overview</span>
          <span className="text-xs bg-blue-500 bg-opacity-20 text-blue-200 px-2 py-1 rounded-full">
            Character-Specific Analysis
          </span>
          {hasUnsavedChanges && (
            <span className="inline-flex items-center space-x-1 bg-amber-500 bg-opacity-20 border border-amber-400 border-opacity-50 text-amber-300 text-xs px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
              <span>Unsaved</span>
            </span>
          )}
        </h3>
        
        <div className="space-y-6">
          {/* Character Name (Auto-filled from selected character) */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Character Name
            </label>
            <input
              type="text"
              value={selectedCharacter?.role?.character_name || data?.character || ''}
              onChange={(e) => handleInputChange('character', e?.target?.value)}
              className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Character name will auto-fill from selected role"
              readOnly={selectedCharacter?.role?.character_name} // Make read-only if character is selected
            />
            {selectedCharacter?.role?.character_name && (
              <p className="text-blue-300 text-xs mt-1">
                Auto-filled from selected character role
              </p>
            )}
          </div>

          {/* Character-specific fields in a 2-column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Emotional Core */}
            <div>
              <label className="block text-sm font-medium text-blue-100 mb-2">
                Emotional Core
              </label>
              <input
                type="text"
                value={data?.emotionalCore || ''}
                onChange={(e) => handleInputChange('emotionalCore', e?.target?.value)}
                className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Primary emotion (e.g., Ambition, Love, Fear)"
              />
            </div>

            {/* Objective */}
            <div>
              <label className="block text-sm font-medium text-blue-100 mb-2">
                Objective
              </label>
              <input
                type="text"
                value={data?.objective || ''}
                onChange={(e) => handleInputChange('objective', e?.target?.value)}
                className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Character's goal (e.g., Prove myself worthy)"
              />
            </div>
          </div>

          {/* Vocal Range - Full width */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Vocal Range
            </label>
            <input
              type="text"
              value={data?.vocalRange || selectedCharacter?.role?.vocal_range || ''}
              onChange={(e) => handleInputChange('vocalRange', e?.target?.value)}
              className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="e.g., C4-G5, Soprano, Baritone"
            />
            {selectedCharacter?.role?.vocal_range && !data?.vocalRange && (
              <p className="text-blue-300 text-xs mt-1">
                Auto-filled from character role: {selectedCharacter?.role?.vocal_range}
              </p>
            )}
          </div>

          {/* Notes Section - Full width */}
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Character Analysis Notes
            </label>
            <textarea
              value={data?.notes || ''}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              className="w-full px-4 py-3 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Additional notes about this character's specific interpretation and performance approach"
              rows={3}
              style={{ whiteSpace: 'pre-wrap' }}
            />
          </div>

          {/* Information Notice */}
          <div className="bg-blue-500 bg-opacity-10 border border-blue-400 border-opacity-30 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-blue-300 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-blue-100 mb-1">
                  Character-Specific Overview
                </h4>
                <p className="text-xs text-blue-200 leading-relaxed">
                  This Overview section focuses on character-specific elements: Character Name (auto-filled), Emotional Core, Objective, and Vocal Range. 
                  Musical and Song Title information is managed in the Song Information section above, as it applies to all character versions of this song.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Per-Level Save / Cancel */}
      <div className="flex justify-end items-center space-x-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => handleCancelLevel?.('overview')}
          className="border-white border-opacity-40 text-white hover:bg-white hover:bg-opacity-10"
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={levelSaving?.overview}
          onClick={() => handleSaveLevel?.('overview')}
          iconName={levelSaved?.overview ? 'Check' : 'Save'}
          iconPosition="left"
          className={`bg-white text-indigo-900 hover:bg-gray-100 ${levelSaving?.overview ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {levelSaving?.overview ? 'Saving...' : levelSaved?.overview ? 'Saved!' : 'Save Overview'}
        </Button>
      </div>
    </div>
  );
};

export default OverviewSectionEdit;